import { Component, OnInit, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, NgForm, NgModel } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { DataService } from 'app/shared/services/dataService';
import { OfrAnnualReportService, OfrAnnualReport, Recommendation } from 'app/shared/services/ofrAnnualReportService';
import { UserService } from 'app/shared/services/userService';
import { Common } from 'app/shared/utility/common';
import { OfrAnnualReportStatus } from 'app/shared/utility/constants';

@Component({
  selector: 'app-annual-report-form',
  templateUrl: './annual-report-form.component.html',
  styleUrls: ['./annual-report-form.component.css']
})
export class AnnualReportFormComponent implements OnInit {
  @ViewChild('ofrAnnualReportForm') ofrAnnualReportForm: NgForm;
  @ViewChild('totalTeamRecommendations') totalTeamRecommendations: any;
  @ViewChild('totalExternalRecommendations') totalExternalRecommendations: any;
  @ViewChildren('MainGoal') mainGoals: QueryList<NgModel>;
  @ViewChildren('TargetAudience') targetAudiences: QueryList<NgModel>;
  @ViewChildren('SupportingEvidence') supportingEvidence: QueryList<NgModel>;
  @ViewChildren('TeamMembers') teamMembers: QueryList<NgModel>;
  @ViewChildren('PartnersAndStakeholders') partnersAndStakeholders: QueryList<NgModel>;
  @ViewChildren('report') reports: QueryList<NgModel>;
  @ViewChildren('reportFO') reportFOs: QueryList<NgModel>;
  @ViewChildren('reportNFO') reportNFOs: QueryList<NgModel>;

  isAdmin: boolean;
  loading: boolean;
  yearOptions: any[];
  report?: OfrAnnualReport;
  showScrollToTop: boolean;
  selectedJurisdiction: string;
  jurisdictionOptions: Array<any> =[];
  selectedYear: number = new Date().getFullYear();

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private dataService: DataService,
    private userService: UserService,
    private ofrAnnualReportService: OfrAnnualReportService
  ) { }

  ngOnInit(): void {
    const years = Common.getYearsDropDown(2022);
    this.yearOptions = [];
    for(let i = 0; i < years.length; i++){
      this.yearOptions.push({ name: years[i].toString(), value: years[i].toString()});
    }

    this.userService.GetUserGroups().then(groups => {
      this.jurisdictionOptions = this.jurisdictionOptions.concat(groups.map((g) => ( { name: g, value:g.replace(' ', '_') } ) ));
   
      if(this.jurisdictionOptions.length === 1){
        this.selectedJurisdiction = this.jurisdictionOptions[0].value;
      }

      this.getOfrAnnualReport();
    });

    this.dataService.getAccess().subscribe((access) => {
      this.isAdmin = access.IsAdmin;
    });
  }

  setScrollToTopVisibility(canShow) {
    this.showScrollToTop = canShow;
  }

  getOfrAnnualReport()
  {
    if (this.selectedJurisdiction)
    {
      this.loading = true;

      this.ofrAnnualReportService.getAnnualReport(this.selectedYear, this.selectedJurisdiction).then(res => {
        if (!this.isAdmin || (this.isAdmin && res && res.Status !== OfrAnnualReportStatus.Draft))
        {
          this.report = res;
        }
        else {
          this.report = null;
        }

        this.loading = false;
      });
    }
  }

  scrollTo(section: string) {
    this.router.navigate([], { fragment: section }).then(res => {
      const element = document.getElementById(section);
      if (element != undefined) element.scrollIntoView();
    });
  }

  isCaseReportDataValid(groupIndex: number, fieldIndex: number) {
    const fieldPrefix = `group${groupIndex}field${fieldIndex}`;
    const reportDataFieldFO: NgModel = this.reportFOs.find(input => input.name === `${fieldPrefix}FatalOverdoses`);
    const reportDataFieldNFO: NgModel = this.reportNFOs.find(input => input.name === `${fieldPrefix}NonFatalOverdoses`);

    if (!reportDataFieldFO || !reportDataFieldNFO) return true;

    return (reportDataFieldFO.pristine && reportDataFieldNFO.pristine) || (!reportDataFieldFO.invalid && !reportDataFieldNFO.invalid);
  }

  isRecommendationSectionValid(index: number, group: string, section: string) {
    let queryList: QueryList<NgModel>;

    switch (section) {
      case 'MainGoal': queryList = this.mainGoals; break;
      case 'TargetAudience': queryList = this.targetAudiences; break;
      case 'SupportingEvidence': queryList = this.supportingEvidence; break;
      case 'TeamMembers': queryList = this.teamMembers; break;
      case 'PartnersAndStakeholders': queryList = this.partnersAndStakeholders; break;
    }

    if (!queryList) return true;

    const recommendationPart: NgModel = queryList.find(input => input.name === `${group}${index}${section}`);

    if (!recommendationPart) return true;

    return recommendationPart.pristine || !recommendationPart.invalid;
  }

  isReportFileValid(reportId: number) {
    const reportFileInput: NgModel = this.reports.find(input => input.name === 'report' + reportId);

    if (!reportFileInput) return true;

    return reportFileInput.pristine || !reportFileInput.invalid;
  }

  public IsReportSubmitted(): boolean {
    return this.report.Status === OfrAnnualReportStatus.Submitted;
  }

  public IsReportDraft(): boolean {
      return this.report.Status === OfrAnnualReportStatus.Draft;
  }

  createOfrAnnualReport()
  {
    this.ofrAnnualReportService.createAnnualReport(this.selectedYear, this.selectedJurisdiction).then(res => {
      this.report = res;
    });
  }

  addExternalRecommendation() {
    if (!this.report.ExternalRecommendations) this.report.ExternalRecommendations = [];
    this.report.ExternalRecommendations.push(new Recommendation());

    this.ofrAnnualReportForm.control.markAllAsTouched();
    this.totalExternalRecommendations.control.markAsDirty({onlySelf: true});
  }

  addTeamRecommendation() {
    if (!this.report.TeamRecommendations) this.report.TeamRecommendations = [];
    this.report.TeamRecommendations.push(new Recommendation());

    this.ofrAnnualReportForm.control.markAllAsTouched();
    this.totalTeamRecommendations.control.markAsDirty({onlySelf: true});
  }

  removeRecommendation(recommendationGroup: string, index: number) {
    if (!confirm("Are you sure you want to remove this recommendation?")) return;

    if (recommendationGroup === "team") {
      this.report.TeamRecommendations.splice(index, 1);
      this.totalTeamRecommendations.control.markAsDirty({onlySelf: true});
    } else if (recommendationGroup === "external") {
      this.report.ExternalRecommendations.splice(index, 1);
      this.totalExternalRecommendations.control.markAsDirty({onlySelf: true});
    }

    this.ofrAnnualReportForm.control.markAllAsTouched();
  }

  markAllControlsAsDirty(abstractControls: AbstractControl[]): void {
    abstractControls.forEach(abstractControl => {
      switch (true) {
        case (abstractControl instanceof FormControl):
          (abstractControl as FormControl).markAsDirty({onlySelf: true});
          break;
        case (abstractControl instanceof FormGroup):
          this.markAllControlsAsDirty(Object.values((abstractControl as FormGroup).controls));
          break;
        case (abstractControl instanceof FormArray):
          this.markAllControlsAsDirty((abstractControl as FormArray).controls);
          break;
      }
    });
  }

  uploadFile(event, reportType:string) {
    var confirmationString = "Confirm that you intend to upload file:\n";

    let file;
    if(event.target.files && event.target.files[0]){
      file = event.target.files[0];
      confirmationString = confirmationString + file.name + "\n";
    }

    if (confirm(confirmationString) == true) {
      var maxSize = 500000;
      if(file.size >= maxSize) {
        event.target.value = '';
        alert("Files over 500 KB can not uploaded.")
        return;
      }

      this.ofrAnnualReportService.uploadAnnualReportFile(file, this.report, reportType)
          .then(res => {
            event.target.value = '';
            this.report = res;
          })
          .catch(error => alert('An error occurred\n' + error.json()));
    }
  }

  downloadFile(event, reportType:string) {
    this.ofrAnnualReportService.downloadAnnualReportFile(this.report.Year.toString(), this.report.Jurisdiction, reportType)
    .then(res => {
      let fileName = this.report.ReportDetails.find(d => d.ReportType == reportType).ReportDisplayName;
      this.downloadBlob(res, fileName);
    })
  }

  deleteFile(event, reportType:string) {
    var confirmationString = "Confirm that you intend to delete this file:";

    if (confirm(confirmationString) == true) {
      this.ofrAnnualReportService.deleteAnnualReportFile(this.report.Year.toString(), this.report.Jurisdiction, reportType)
          .then(res => {
            this.report = res;
          })
          .catch(error => alert('An error occurred\n' + error.json()));
    }
  }

  downloadBlob(data: any, name: any) {
    let newBlob = new Blob([data], { type: data.type });
    if (typeof window.navigator.msSaveBlob !== 'undefined'){ //Stupid windows only hack (IE and Edge)
      window.navigator.msSaveBlob(newBlob, name);
    } else {
      let a = document.createElement("a");
      document.body.appendChild(a);
      a.setAttribute("style", "display: none");
      let url = window.URL.createObjectURL(newBlob);
      a.href = url;
      a.download = name;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  }

  printReport() {
    setTimeout(() => {
      window.print();
    }, 500);
  }

  saveOfrAnnualReport(e) {
    this.ofrAnnualReportService.saveAnnualReport(this.report).then(res => {
      alert("Changes to the report have been saved");
    });

    e.target.blur();
  }

  submitOfrAnnualReport(e) {
    if (this.ofrAnnualReportForm.valid)
    {
      this.ofrAnnualReportService.submitAnnualReport(this.report).then(res => {
        this.report = res;
        alert("The report has been submitted");
      });
    }
    else
    {
      const formGroup = this.ofrAnnualReportForm.form;
      formGroup.markAllAsTouched();
      this.markAllControlsAsDirty(Object.values(formGroup.controls));
      this.snackBar.open('Please complete/fix all fields. Errors are identified with a message.', 'close',  {duration: 3000});
    }

    e.target.blur();
  }
}